/**
 * Trustee-narrated case study blog posts. Anonymized details; illustrative of
 * Guild counterclaim + trustee-pledge process. Rendered on /blog and /blog/:slug.
 * SEO-optimized with unique title/description, Article JSON-LD, canonical, images.
 */
export const POSTS = [
  {
    slug: "47-day-ohio-sheriff-sale-halted",
    title: "The 47-Day Ohio Save — How a Cleveland Family Beat a Scheduled Sheriff Sale",
    excerpt:
      "Cuyahoga County trustee file: a Cleveland family walks into the Guild sixty-one days before a scheduled sheriff sale. Forty-seven days later, the sale is off the calendar and the mortgage is reassigned.",
    date: "2026-02-10",
    author: "Senior Trustee · Ohio Desk",
    readMinutes: 6,
    cover: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1400&q=85",
    coverAlt: "Cleveland courthouse steps on a winter morning",
    tags: ["Ohio", "Sheriff Sale", "Counterclaim", "60-day"],
    metaDescription:
      "Trustee case file: How the Azure Gypsy Guild filed a counterclaim in Cuyahoga County that halted a scheduled sheriff sale and reassigned a Cleveland family's mortgage in 47 days.",
    body: [
      {
        h: "Intake",
        p: [
          "The family was referred by a former member. Notice of sheriff sale had been posted 22 days earlier; the sale date was 61 days out. Two prior loan-modification attempts had been denied by the servicer. A Chapter 13 conversation with a local bankruptcy attorney was on the table but hadn't been filed.",
          "The trustee opened the emergency file the same afternoon. Six agreements were e-signed inside the private portal the same evening. The mortgage statement, deed, government ID, and the foreclosure complaint were uploaded within 48 hours.",
        ],
      },
      {
        h: "The ledger audit",
        p: [
          "The good accounting ledger audit ran over ten business days. It reconciled every payment posted since 2016 against the original note, the two loan modifications, and the servicer's payment history. Three separate posting errors totaling $4,207.14 were identified. More importantly, the audit surfaced a break in the chain of title between the original lender and the current servicer's assignor — a break the servicer had not disclosed in the foreclosure complaint.",
        ],
      },
      {
        h: "The counterclaim",
        p: [
          "Day 22 of the case, the verified counterclaim was filed with the Cuyahoga County Court of Common Pleas. It named the law firm and the bank. It challenged standing on the basis of the chain-of-title gap, requested an accounting reconciliation to the original note, and asked for injunctive relief pausing the sheriff sale until the counterclaim was heard.",
          "The sheriff sale was pulled from the calendar the same week the injunctive-relief motion was filed. That is the outcome we expect in every well-prepared Ohio case, and it is why we file inside the statutory window rather than waiting.",
        ],
      },
      {
        h: "Settlement",
        p: [
          "Day 41 of the case, a mediation session was held with the law firm's counterclaim response team. The trustee pledged funds equal to the reconciled accounting balance. The counterclaim was resolved via a stipulated settlement: the mortgage was reassigned, the foreclosure was voluntarily dismissed by the plaintiff, and a manual tracking number was stamped on the Guild case file.",
          "Total elapsed time from intake: 47 days. Total member cost: $1,500 membership plus 1.5% of the reconciled mortgage balance, paid only at settlement.",
        ],
      },
      {
        h: "What made this case work",
        p: [
          "Two things. First: the family enrolled the moment they had the notice, not the week before the sale. Every extra day is another day the counterclaim can be prepared. Second: the ledger audit was complete before the counterclaim was filed. Filing a counterclaim without an audit is asking the court to take your word for it. Filing one with a fully reconciled audit is asking the court to enforce your rights.",
        ],
      },
    ],
  },
  {
    slug: "texas-counterclaim-without-bankruptcy",
    title: "Texas File #024 — A $487k Counterclaim Settlement Without Bankruptcy",
    excerpt:
      "Harris County substitute trustee sale, thirty-nine days out. The homeowner had two prior Chapter 13 dismissals on record. The Guild found a third path.",
    date: "2026-01-28",
    author: "Senior Trustee · Texas Desk",
    readMinutes: 5,
    cover: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1400&q=85",
    coverAlt: "Suburban Texas neighborhood at dusk",
    tags: ["Texas", "No-Bankruptcy", "Chain of Title", "Counterclaim"],
    metaDescription:
      "Trustee case file: Harris County homeowner facing a $487,000 mortgage foreclosure and two prior Chapter 13 dismissals. The Guild's counterclaim + trustee-pledge process settled the case in 58 days — with no new bankruptcy filing.",
    body: [
      {
        h: "The situation",
        p: [
          "A Harris County homeowner had lived in the property for 19 years. Two prior Chapter 13 filings — both dismissed for missed plan payments — had left them ineligible for the automatic stay under §362(c)(4). A substitute trustee sale was scheduled for the first Tuesday of the following month.",
          "A Chapter 7 filing would have required surrendering the property. The homeowner had significant non-exempt equity. Bankruptcy was, structurally, not the right tool.",
        ],
      },
      {
        h: "Why Texas is the fastest state",
        p: [
          "Texas allows non-judicial foreclosure with as little as 41 days from Notice of Default to sale. That speed cuts both ways. It means the sale can come fast — but it also means the counterclaim, once filed in Texas district court, forces a fast response from the law firm.",
          "In this case, the counterclaim was filed 11 days before the scheduled sale. A temporary restraining order was granted the following business day. The sale was removed from the courthouse-steps docket.",
        ],
      },
      {
        h: "The ledger reveals the tranche",
        p: [
          "The good accounting ledger audit traced the loan into an SEC-registered mortgage-backed security tranche assigned in 2019. The audit surfaced a discrepancy: the assignment recorded in Harris County named an entity that had ceased to exist prior to the recording date. The servicer had not disclosed this in the substitute trustee's notice.",
          "That single discovery was the pivot point of the settlement.",
        ],
      },
      {
        h: "The pledge and the reassignment",
        p: [
          "The Guild trustee account pledged funds sufficient to reconcile the accounting to a settlement figure that reflected the true balance minus the audit adjustments and the disclosure gap. The counterclaim was resolved via stipulated settlement. The mortgage was reassigned. The homeowner remains in the property.",
          "Total elapsed time: 58 days. No bankruptcy on the credit report. No non-exempt equity surrendered.",
        ],
      },
    ],
  },
  {
    slug: "south-miami-judicial-foreclosure-30-days",
    title: "South Miami — A Judicial Foreclosure Halted in Thirty Days",
    excerpt:
      "Miami-Dade County. A judicial foreclosure filed by a national servicer. The counterclaim went in ten days after intake. The case is now closed by voluntary dismissal.",
    date: "2026-01-14",
    author: "Senior Trustee · Florida Desk",
    readMinutes: 4,
    cover: "https://images.unsplash.com/photo-1571079570759-18e5342e5b8b?w=1400&q=85",
    coverAlt: "South Miami residential neighborhood",
    tags: ["Florida", "Judicial", "Voluntary Dismissal", "30-day"],
    metaDescription:
      "Trustee case file: Miami-Dade judicial foreclosure filed by a national servicer, resolved by voluntary dismissal 30 days after the Guild's counterclaim landed in the same case number.",
    body: [
      {
        h: "Why Florida is structurally friendly to counterclaims",
        p: [
          "Florida is a judicial foreclosure state. That means the bank has to sue the homeowner in circuit court before any foreclosure sale can happen. Once they've filed, we can counter-sue in the same case. There is no separate filing to open. There is no jurisdictional argument to prevail on. The court that hears the foreclosure is the court that hears the counterclaim.",
          "That structural advantage is why Florida cases resolve on the fastest end of the Guild average.",
        ],
      },
      {
        h: "The audit finding",
        p: [
          "The ledger audit reconciled the loan to the original 2007 note. It surfaced a chain-of-title issue in an assignment filed in 2013. The assignment named an assignor entity that had merged with a successor two years earlier — a merger the recorded assignment did not disclose.",
          "The counterclaim was filed inside the existing case number. The response deadline forced the plaintiff's counsel into a fast conversation.",
        ],
      },
      {
        h: "Voluntary dismissal",
        p: [
          "Twenty-eight days after the counterclaim was filed, the plaintiff filed a voluntary dismissal of the foreclosure action. The Guild trustee account settled the reconciled accounting. The mortgage was reassigned. The homeowner remains in the property.",
          "Total elapsed time from intake to voluntary dismissal: 30 days.",
        ],
      },
    ],
  },
];

export const POST_SLUGS = POSTS.map((p) => p.slug);
export const getPost = (slug) => POSTS.find((p) => p.slug === slug);
